import React, { MouseEvent, useContext, useMemo, useState } from "react";

import { faBracketsCurly } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Col from "antd/es/col";
import Dropdown from "antd/es/dropdown";
import Row from "antd/es/row";
import Tooltip from "antd/es/tooltip";
import { Descendant, Editor, Transforms } from "slate";
import { ReactEditor, useSlate } from "slate-react";

import EmailDynamicVariable from "@mapmycustomers/shared/types/email/EmailDynamicVariable";
import { EntityTypesSupportedByEmailFeature } from "@mapmycustomers/shared/types/entity";
import useDynamicCallback from "@mapmycustomers/shared/util/hook/useDynamicCallback";

import { useConfigProvider } from "../../../../ConfigProvider";
import SearchField from "../../../../SearchField";
import ElementType from "../../../../type/slate/ElementType";
import EmailDynamicVariableChip from "../../../EmailDynamicVariableChip";
import EmailContext from "../../../EmailEditorsContext";
import ActionCallback from "../../type/ActionCallback";
import getFocusedEditor from "../../util/getFocusedEditor";
import ToggleButton from "../ToggleButton";

import styles from "./DynamicVariables.module.scss";

interface DynamicVariablesProps {
  dynamicVariables: EmailDynamicVariable[];
  entityType: EntityTypesSupportedByEmailFeature;
  onAction?: ActionCallback;
}

const DynamicVariables = ({ dynamicVariables, entityType, onAction }: DynamicVariablesProps) => {
  const bodyEditor = useSlate();
  const emailContext = useContext(EmailContext);

  const configProvider = useConfigProvider();

  const [searchText, setSearchText] = useState<string>("");
  const [dropDownOpen, setDropDownOpen] = useState<boolean>(false);

  const filteredVariants = useMemo(() => {
    const text = searchText.trim().toLowerCase();
    return dynamicVariables.filter(({ displayName }) => {
      return displayName.toLowerCase().includes(text);
    });
  }, [searchText, dynamicVariables]);

  const handleVariantClick = useDynamicCallback(
    (e: MouseEvent, { name, prefix }: EmailDynamicVariable) => {
      e.preventDefault();

      const editor = getFocusedEditor(bodyEditor, emailContext.subjectEditor);

      setDropDownOpen(false);
      const insert: Descendant[] = [
        {
          children: [{ text: "" }],
          name,
          prefix,
          type: ElementType.DYNAMIC_VARIABLE,
          value: `{{${prefix}:${entityType}:${name}}}`,
        },
        { text: "" },
      ];
      Transforms.insertNodes(editor, insert);
      Transforms.move(editor, { edge: "end" });

      // move cursor to the end of the inserted dynamic variable
      if (editor.selection) {
        const path = editor.selection.anchor.path;
        Transforms.select(editor, Editor.end(editor, path));
      }

      if (ReactEditor.isFocused(editor)) {
        ReactEditor.focus(editor);
      }

      onAction?.("Insert Dynamic Variable");
    }
  );

  const handleMouseDown = useDynamicCallback((e: MouseEvent) => {
    e.preventDefault();
  });

  const handleButtonClick = useDynamicCallback(() => {
    setDropDownOpen(!dropDownOpen);
  });

  const dropdownRender = useDynamicCallback(() => (
    <div className={styles.overlayContainer}>
      <div className={styles.overlaySearchContainer}>
        <SearchField
          onChange={setSearchText}
          placeholder={configProvider.formatMessage(
            "ui.emailBody.actions.dynamicVariables.searchPlaceholder"
          )}
          value={searchText}
        />
      </div>
      <div className={styles.overlayList}>
        {filteredVariants.length === 0 && (
          <div className={styles.noResult}>
            <div className={styles.noResultTitle}>
              {configProvider.formatMessage("ui.emailBody.actions.dynamicVariables.noResult.title")}
            </div>
            <div className={styles.noResultSubTitle}>
              {configProvider.formatMessage(
                "ui.emailBody.actions.dynamicVariables.noResult.subTitle"
              )}
            </div>
          </div>
        )}
        {filteredVariants.map((filteredVariant) => (
          <Row
            className={styles.overlayItem}
            key={filteredVariant.name}
            onClick={(e) => handleVariantClick(e, filteredVariant)}
            onMouseDown={handleMouseDown}
          >
            <Col offset={2} span={11}>
              <Tooltip
                title={
                  filteredVariant.allEntitiesHaveValues
                    ? undefined
                    : configProvider.formatMessage(
                        "ui.emailBody.actions.dynamicVariables.someEntityDontHaveValue"
                      )
                }
                trigger={["hover"]}
              >
                <span>
                  <EmailDynamicVariableChip
                    allEntitiesHaveValues={filteredVariant.allEntitiesHaveValues}
                  >
                    {filteredVariant.displayName}
                  </EmailDynamicVariableChip>
                </span>
              </Tooltip>
            </Col>
            <Col className={styles.overlayItemValue} span={11}>
              {filteredVariant.displayValue}
            </Col>
          </Row>
        ))}
      </div>
    </div>
  ));

  return (
    <Dropdown
      dropdownRender={dropdownRender}
      onOpenChange={setDropDownOpen}
      open={dropDownOpen}
      placement="topRight"
      trigger={["click"]}
    >
      <Tooltip title={configProvider.formatMessage("ui.emailBody.actions.insertDynamicVar")}>
        <ToggleButton
          active={dropDownOpen}
          onClick={handleButtonClick}
          onMouseDown={handleMouseDown}
        >
          <FontAwesomeIcon icon={faBracketsCurly} />
        </ToggleButton>
      </Tooltip>
    </Dropdown>
  );
};

export default DynamicVariables;
